import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Send, Trash2, Edit2, Eye, ArrowLeft, Loader2, ClipboardList, CheckCircle2, X, Download, Filter } from "lucide-react";
import { SortableTh, useTableSort, sortRows } from "@/components/SortableTh";
import { useUnsavedChanges, useUnsavedControls } from "@/lib/unsavedChanges";
import { UnsavedDecisionDialog } from "@/components/UnsavedDecisionDialog";
import { ProductStatus } from "@/components/ProductStatus";
import { OrderEmailRequiredDialog } from "@/components/OrderEmailRequiredDialog";
import { OrderSendDialog } from "@/components/OrderSendDialog";
import { isUsableEmailAddress } from "@/lib/emailValidation";

const UDM_OPTIONS = ["gr", "kg", "mg", "ml", "cl", "L", "pz", "ct", "conf", "gallone"];

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return String(iso).slice(0, 10); }
};

const UdmSelect = ({ value, onChange, testId }) => (
  <select className={`input-luxury !py-1.5 !px-2 text-sm w-full ${!value ? "product-ux-select-empty" : ""}`} value={value || ""} onChange={onChange} data-testid={testId}>
    <option value="">—</option>
    {UDM_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
  </select>
);

const StatusBadge = ({ status, testId }) => (
  status === "sent"
    ? <ProductStatus tone="info" icon={CheckCircle2} testId={testId}>Inviato</ProductStatus>
    : <ProductStatus tone="neutral" testId={testId}>Bozza</ProductStatus>
);

const RoleBadge = ({ role, testId }) => (
  <ProductStatus tone={role === "packaging" ? "info" : role === "custom" ? "neutral" : "success"} testId={testId}>
    {role === "packaging" ? "Packaging" : role === "custom" ? "Altro" : "Materia Prima"}
  </ProductStatus>
);

const emptyCustom = () => ({ name: "", code: "", supplier_role: "", format: "", quantity: "", udm: "" });
const emptyFilters = () => ({ brand_id: "", category_id: "", subcategory_id: "", code: "", supplier_code: "", format: "" });
const orderSnapshot = (rows, customRows, notes) => JSON.stringify({ rows, customRows, notes });
const showCustomRole = (item) => item.supplier_role || (item.is_custom || !item.product_id ? "custom" : "raw_material");

const keepRoleVisible = (field) => {
  const viewport = field?.closest('[data-testid="order-custom-lines-scroll"]');
  if (!viewport) return;
  const outer = viewport.getBoundingClientRect(), inner = field.getBoundingClientRect();
  if (inner.left < outer.left + 8) viewport.scrollLeft += inner.left - outer.left - 8;
  else if (inner.right > outer.right - 8) viewport.scrollLeft += inner.right - outer.right + 8;
};

export default function SupplierOrders({ supplier, canWrite, subscriptionDisabled = false }) {
  const sid = supplier.id;
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list"); // list | form | detail
  const [editingId, setEditingId] = useState(null);
  const [rows, setRows] = useState({});         // pid -> { checked, quantity, udm }
  const [customRows, setCustomRows] = useState([]);
  const [notes, setNotes] = useState("");
  const [baseline, setBaseline] = useState(() => orderSnapshot({}, [], ""));
  const [confirmBack, setConfirmBack] = useState(false);
  const [formError, setFormError] = useState("");
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);
  const [sendModal, setSendModal] = useState(null); // { mode:'form'|'existing', orderId?, email }
  const [emailPrompt, setEmailPrompt] = useState(null);
  const [emailChecking, setEmailChecking] = useState(false);
  const sendTriggerRef = useRef(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState(emptyFilters());
  const { sortField, sortDir, toggleSort } = useTableSort();
  const sortedOrders = useMemo(() => sortRows(orders, sortField, sortDir, {
    order_number: o => o.order_number || "",
    created_at: o => new Date(o.created_at || 0).getTime(),
    status: o => o.status || "",
    items: o => (o.items || []).length,
  }), [orders, sortField, sortDir]);

  const load = async () => {
    setLoading(true);
    try {
      const [o, p, b, c] = await Promise.all([
        api.get(`/suppliers/${sid}/orders`),
        api.get(`/suppliers/${sid}/products`),
        api.get(`/brands`),
        api.get(`/categories`),
      ]);
      setOrders(o.data);
      setProducts(p.data);
      setBrands(b.data);
      setCats(c.data);
    } catch { toast.error("Errore caricamento ordini"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sid]);

  useEffect(() => {
    if (view !== "form") return undefined;
    const reposition = () => {
      const focused = document.activeElement;
      if (focused?.getAttribute?.("data-testid")?.startsWith("order-custom-role-")) keepRoleVisible(focused);
    };
    window.addEventListener("resize", reposition);
    return () => window.removeEventListener("resize", reposition);
  }, [view]);

  useEffect(() => {
    if (!loading && canWrite && sp.get("new") === "1") {
      openNew();
      const n = new URLSearchParams(sp);
      n.delete("new");
      setSp(n, { replace: true });
    }
    // eslint-disable-next-line
  }, [loading, sp]);

  const openNew = () => { setEditingId(null); setRows({}); setCustomRows([]); setNotes(""); setFormError(""); setBaseline(orderSnapshot({}, [], "")); setFilters(emptyFilters()); setShowFilters(false); setView("form"); };
  const openEdit = (o) => {
    const rmap = {}; const customs = [];
    (o.items || []).forEach(it => {
      if (it.is_custom || !it.product_id) {
        customs.push({ name: it.product_name || it.supplier_product_name || "", code: it.product_code || it.supplier_code || "",
          supplier_role: it.supplier_role || "custom", format: it.format || "", quantity: it.quantity, udm: it.udm || "" });
      } else {
        const associationId = `${it.product_id}__${it.supplier_role || "raw_material"}`;
        rmap[associationId] = { checked: true, quantity: it.quantity, udm: it.udm || "" };
      }
    });
    setEditingId(o.id); setRows(rmap); setCustomRows(customs); setNotes(o.notes || ""); setFormError(""); setBaseline(orderSnapshot(rmap, customs, o.notes || "")); setFilters(emptyFilters()); setShowFilters(false); setView("form");
  };
  const openDetail = (o) => { setDetail(o); setView("detail"); };

  // ---- Reference maps + supplier-scoped filter options / filtered list ----
  const brandMap = useMemo(() => Object.fromEntries(brands.map(b => [b.id, b])), [brands]);
  const catMap = useMemo(() => Object.fromEntries(cats.map(c => [c.id, c])), [cats]);

  // Options are limited to values present among THIS supplier's products.
  const brandOptions = useMemo(() => {
    const ids = new Set(products.map(p => p.brand_id).filter(Boolean));
    return brands.filter(b => ids.has(b.id));
  }, [products, brands]);
  const catOptions = useMemo(() => {
    const ids = new Set(products.map(p => p.category_id).filter(Boolean));
    return cats.filter(c => !c.parent_id && ids.has(c.id));
  }, [products, cats]);
  const subcatOptions = useMemo(() => {
    const ids = new Set(products.map(p => p.subcategory_id).filter(Boolean));
    return cats.filter(c => c.parent_id && ids.has(c.id)
      && (!filters.category_id || c.parent_id === filters.category_id));
  }, [products, cats, filters.category_id]);

  const filtersActive = useMemo(() => Object.values(filters).some(v => v !== ""), [filters]);

  const filteredProducts = useMemo(() => {
    const f = filters;
    return products.filter(p => {
      if (f.brand_id && p.brand_id !== f.brand_id) return false;
      if (f.category_id && p.category_id !== f.category_id) return false;
      if (f.subcategory_id && p.subcategory_id !== f.subcategory_id) return false;
      if (f.code && !(p.code || "").toLowerCase().includes(f.code.toLowerCase())) return false;
      if (f.supplier_code && !(p.supplier_raw_material_code || "").toLowerCase().includes(f.supplier_code.toLowerCase())) return false;
      if (f.format && !(p.supplier_format || "").toLowerCase().includes(f.format.toLowerCase())) return false;
      return true;
    });
  }, [products, filters]);

  const clearFilters = () => setFilters(emptyFilters());
  const filterChips = [];
  if (filters.brand_id) filterChips.push({ key: "brand_id", label: `Brand: ${brandMap[filters.brand_id]?.name || filters.brand_id}` });
  if (filters.category_id) filterChips.push({ key: "category_id", label: `Categoria: ${catMap[filters.category_id]?.name || filters.category_id}` });
  if (filters.subcategory_id) filterChips.push({ key: "subcategory_id", label: `Sotto-categoria: ${catMap[filters.subcategory_id]?.name || filters.subcategory_id}` });
  if (filters.code) filterChips.push({ key: "code", label: `Codice: ${filters.code}` });
  if (filters.supplier_code) filterChips.push({ key: "supplier_code", label: `Codice Fornitore: ${filters.supplier_code}` });
  if (filters.format) filterChips.push({ key: "format", label: `Formato: ${filters.format}` });
  const removeFilter = (key) => setFilters(f => {
    const next = { ...f };
    if (key === "category_id") { next.category_id = ""; next.subcategory_id = ""; }
    else next[key] = "";
    return next;
  });

  const associationKey = (product) => product.association_id || `${product.id}__${product.supplier_role || "raw_material"}`;
  const rowFor = (p) => rows[associationKey(p)] || { checked: false, quantity: "", udm: p.supplier_order_udm || "" };
  const setRow = (key, patch) => setRows(m => ({ ...m, [key]: { ...(m[key] || {}), ...patch } }));
  const toggleRow = (p) => {
    const key = associationKey(p);
    const cur = rows[key];
    if (cur?.checked) setRow(key, { checked: false });
    else setRow(key, { checked: true, quantity: cur?.quantity ?? "", udm: cur?.udm ?? (p.supplier_order_udm || "") });
  };

  const setCustom = (i, patch) => setCustomRows(list => list.map((c, idx) => idx === i ? { ...c, ...patch } : c));
  const addCustom = () => setCustomRows(list => [...list, emptyCustom()]);
  const removeCustom = (i) => setCustomRows(list => list.filter((_, idx) => idx !== i));

  const buildItems = () => {
    const items = [];
    products.forEach(p => {
      const r = rows[associationKey(p)];
      if (r?.checked && Number(r.quantity) > 0) {
        items.push({
          product_id: p.id,
          supplier_role: p.supplier_role || "raw_material",
          quantity: Number(r.quantity),
          udm: r.udm ?? (p.supplier_order_udm || ""),
        });
      }
    });
    customRows.forEach(c => {
      if ((c.name?.trim() || c.code?.trim()) && Number(c.quantity) > 0) {
        items.push({ product_id: null, name: c.name.trim(), code: c.code.trim(), supplier_role: c.supplier_role,
          format: c.format?.trim() || "", quantity: Number(c.quantity), udm: c.udm || "" });
      }
    });
    return items;
  };
  const selectedCount = buildItems().length;

  const validateOrder = () => {
    const reject = message => { setFormError(message); return false; };
    setFormError("");
    if (Object.values(rows).some(r => r.checked && !(Number(r.quantity) > 0 && Number.isFinite(Number(r.quantity))))) {
      return reject("Inserisci una quantità valida per ogni prodotto selezionato");
    }
    if (customRows.some(c => !(c.name?.trim() || c.code?.trim()) || !(Number(c.quantity) > 0 && Number.isFinite(Number(c.quantity))))) {
      return reject("Completa o rimuovi le righe personalizzate senza nome/codice o quantità");
    }
    if (customRows.some(c => !["raw_material", "packaging", "custom"].includes(c.supplier_role))) {
      return reject("Seleziona la tipologia per ogni riga aggiunta");
    }
    if (selectedCount === 0) return reject("Seleziona almeno un prodotto (o aggiungi una riga) con quantità");
    return true;
  };

  const persist = async () => {
    const items = buildItems();
    if (items.length === 0) return null;
    const body = { notes, items };
    if (editingId) { await api.put(`/suppliers/${sid}/orders/${editingId}`, body); return editingId; }
    const r = await api.post(`/suppliers/${sid}/orders`, body); return r.data.id;
  };

  const saveDraftForExit = async () => {
    if (!validateOrder()) return false;
    setBusy(true);
    try {
      const id = await persist();
      setEditingId(id);
      setBaseline(orderSnapshot(rows, customRows, notes));
      toast.success("Bozza salvata");
      return true;
    } catch (e) {
      const message = e?.response?.data?.detail || "Errore salvataggio";
      setFormError(message); toast.error(message); return false;
    }
    finally { setBusy(false); }
  };

  const saveDraft = async () => {
    if (await saveDraftForExit()) { setView("list"); load(); }
  };

  const dirty = view === "form" && canWrite && baseline !== orderSnapshot(rows, customRows, notes);
  const unsaved = useUnsavedControls();
  const orderPrompt = {
    title: "Ordine non salvato",
    description: editingId
      ? "Hai modifiche non salvate. Annullando, la bozza già salvata resterà nello storico senza queste modifiche."
      : "Hai un ordine non salvato. Puoi salvarlo come bozza oppure annullarlo.",
    saveLabel: "Salva in bozza", discardLabel: "Annulla ordine", stayLabel: "Resta qui", error: formError,
  };
  useUnsavedChanges(dirty, saveDraftForExit, () => {
    const saved = JSON.parse(baseline);
    setRows(saved.rows); setCustomRows(saved.customRows); setNotes(saved.notes);
  }, orderPrompt);

  const backToList = () => {
    if (unsaved?.anyDirty()) setConfirmBack(true);
    else setView("list");
  };
  const saveAndBack = async () => {
    if (await unsaved.saveAll()) { setConfirmBack(false); setView("list"); load(); }
  };
  const discardAndBack = () => {
    unsaved.discardAll(); setConfirmBack(false); setView("list");
  };

  const checkOrderEmail = async (next) => {
    sendTriggerRef.current = document.activeElement;
    setEmailChecking(true);
    try {
      const response = await api.get("/me/tenant");
      const orderEmail = (response.data.order_email || "").trim();
      if (!orderEmail) setEmailPrompt(next);
      else if (!isUsableEmailAddress(orderEmail)) setEmailPrompt({
        ...next, reason: "ORDER_EMAIL_INVALID",
        message: "Correggi l'email ordini nell'anagrafica prima di inviare l'ordine.",
      });
      else setSendModal({ ...next, orderEmail });
    } catch { toast.error("Impossibile verificare l'email ordini. Riprova."); }
    finally { setEmailChecking(false); }
  };

  const openSendFromForm = () => {
    if (!validateOrder()) return;
    checkOrderEmail({ mode: "form", email: supplier.email || "" });
  };
  const openSendExisting = (orderId) => checkOrderEmail({ mode: "existing", orderId, email: supplier.email || "" });

  const renderOrderEmailPrompt = () => emailPrompt && (
    <OrderEmailRequiredDialog mode={emailPrompt.mode} reason={emailPrompt.reason} message={emailPrompt.message}
      returnFocusRef={sendTriggerRef} onCancel={() => setEmailPrompt(null)}
      onConfigure={() => {
        setEmailPrompt(null);
        navigate("/profile#email-ordini", { state: { returnTo: `/suppliers/${sid}?tab=ordini` } });
      }} />
  );

  const confirmSend = async () => {
    const email = (sendModal.email || "").trim();
    if (!isUsableEmailAddress(email)) { toast.error("Inserisci un indirizzo email valido"); return; }
    setBusy(true);
    let oid = sendModal.orderId;
    try {
      if (sendModal.mode === "form") {
        oid = await persist();
        setEditingId(oid);
        setBaseline(orderSnapshot(rows, customRows, notes));
      }
      await api.post(`/suppliers/${sid}/orders/${oid}/send`, { email });
      toast.success("Ordine inviato al fornitore");
      setSendModal(null); setView("list"); load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "ORDER_EMAIL_REQUIRED" || detail?.code === "ORDER_EMAIL_INVALID") {
        setSendModal(null);
        setEmailPrompt({ mode: "existing", orderId: oid, email, reason: detail.code, message: detail.message });
      } else toast.error(typeof detail === "string" ? detail : "Invio fallito");
    }
    finally { setBusy(false); }
  };

  const renderSendModal = () => sendModal && (
    <OrderSendDialog email={sendModal.email} orderEmail={sendModal.orderEmail} busy={busy}
      returnFocusRef={sendTriggerRef} onCancel={() => setSendModal(null)}
      onEmailChange={email => setSendModal(m => ({ ...m, email }))} onConfirm={confirmSend} />
  );

  const removeOrder = async (o) => {
    if (!window.confirm("Eliminare questo ordine? L'operazione non è reversibile.")) return;
    try { await api.delete(`/suppliers/${sid}/orders/${o.id}`); toast.success("Ordine eliminato"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore eliminazione"); }
  };

  const downloadPdf = async (o) => {
    try {
      const res = await api.get(`/suppliers/${sid}/orders/${o.id}/pdf`, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const fname = m ? m[1] : "ordine.pdf";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data); a.download = fname;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(a.href);
    } catch { toast.error("Errore download PDF"); }
  };

  if (loading) return <div className="text-sm text-[#707070] p-2">Caricamento ordini...</div>;

  // ------------------------------ FORM ------------------------------
  if (view === "form") {
    return (
      <div data-testid="order-form">
        <div className="flex items-center justify-between mb-4">
          <button onClick={backToList} className="inline-flex items-center gap-1.5 text-sm text-[#707070] hover:text-[#1A1A1A]" data-testid="order-form-back">
            <ArrowLeft className="w-3.5 h-3.5"/> Torna allo storico
          </button>
          <div className="text-[11px] text-[#707070]" data-testid="order-line-count">{selectedCount} rig{selectedCount === 1 ? "a" : "he"} nell'ordine</div>
        </div>

        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] tracking-luxury text-[#707070] uppercase">
            Prodotti del fornitore
            {filtersActive && products.length > 0 && (
              <span className="ml-2 normal-case tracking-normal text-[#B45309]">
                {filteredProducts.length} di {products.length}
              </span>
            )}
          </div>
          {products.length > 0 && (
            <button onClick={()=>setShowFilters(s=>!s)}
                    className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 border transition ${showFilters ? "bg-[#1A1A1A] text-[#FAF9F6] border-[#1A1A1A]" : (filterChips.length ? "border-[#B45309] text-[#B45309]" : "border-[#E5E5E0] hover:border-[#C5B39B]")}`}
                    data-testid="order-toggle-filters-btn">
              <Filter className="w-3 h-3" strokeWidth={1.5}/>
              {showFilters ? "Chiudi filtri" : (filterChips.length ? `Filtri (${filterChips.length})` : "Filtri")}
            </button>
          )}
        </div>

        {showFilters && products.length > 0 && (
          <div className="border border-[#E5E5E0] bg-[#FBFAF7] p-4 mb-3" data-testid="order-filters-panel">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="label-tiny">Brand</label>
                <select className={`input-luxury !py-1.5 ${!filters.brand_id ? "product-ux-select-empty" : ""}`} value={filters.brand_id}
                        data-testid="order-filter-brand"
                        onChange={e=>setFilters(f=>({...f, brand_id: e.target.value}))}>
                  <option value="">Tutti</option>
                  {brandOptions.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div>
                <label className="label-tiny">Categoria</label>
                <select className={`input-luxury !py-1.5 ${!filters.category_id ? "product-ux-select-empty" : ""}`} value={filters.category_id}
                        data-testid="order-filter-category"
                        onChange={e=>setFilters(f=>({...f, category_id: e.target.value, subcategory_id: ""}))}>
                  <option value="">Tutte</option>
                  {catOptions.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="label-tiny">Sotto-categoria</label>
                <select className={`input-luxury !py-1.5 ${!filters.subcategory_id ? "product-ux-select-empty" : ""}`} value={filters.subcategory_id}
                        data-testid="order-filter-subcategory"
                        disabled={!filters.category_id}
                        onChange={e=>setFilters(f=>({...f, subcategory_id: e.target.value}))}>
                  <option value="">{filters.category_id ? "Tutte" : "Seleziona prima una categoria"}</option>
                  {subcatOptions.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="label-tiny">Codice prodotto</label>
                <input className="input-luxury !py-1.5" value={filters.code} data-testid="order-filter-code" placeholder="es. PRD-001"
                       onChange={e=>setFilters(f=>({...f, code: e.target.value}))} />
              </div>
              <div>
                <label className="label-tiny">Codice Fornitore</label>
                <input className="input-luxury !py-1.5" value={filters.supplier_code} data-testid="order-filter-supplier-code" placeholder="Codice del fornitore"
                       onChange={e=>setFilters(f=>({...f, supplier_code: e.target.value}))} />
              </div>
              <div>
                <label className="label-tiny">Formato</label>
                <input className="input-luxury !py-1.5" value={filters.format} data-testid="order-filter-format" placeholder="es. 1 kg"
                       onChange={e=>setFilters(f=>({...f, format: e.target.value}))} />
              </div>
            </div>
            <div className="flex justify-end mt-3">
              <button onClick={clearFilters} className="text-[10px] tracking-luxury uppercase text-[#8C3A3A] hover:opacity-70" data-testid="order-filters-clear-btn">Pulisci filtri</button>
            </div>
          </div>
        )}

        {filterChips.length > 0 && (
          <div className="flex items-center flex-wrap gap-2 mb-3" data-testid="order-active-filters">
            <span className="text-[10px] tracking-luxury uppercase text-[#707070] mr-1">Filtri attivi:</span>
            {filterChips.map(c => (
              <button key={c.key} onClick={()=>removeFilter(c.key)}
                      className="inline-flex items-center gap-1.5 text-xs pl-3 pr-2 py-1 bg-white border border-[#E5E5E0] rounded-full hover:border-[#C5B39B] transition"
                      data-testid={`order-active-filter-${c.key}`}>
                {c.label}
                <X className="w-3 h-3 text-[#707070]" strokeWidth={2}/>
              </button>
            ))}
            <button onClick={clearFilters} className="text-[10px] tracking-luxury uppercase text-[#8C3A3A] hover:opacity-70 ml-1" data-testid="order-active-filters-clear">Pulisci tutto</button>
          </div>
        )}

        {products.length === 0 ? (
          <div className="text-sm text-[#707070] mb-4">Nessun prodotto associato a questo fornitore.</div>
        ) : (
          <div className="border border-[#E5E5E0] max-h-[36vh] overflow-auto mb-5">
            <table className="w-full min-w-[56rem] text-sm">
              <thead className="sticky top-0 bg-white z-10">
                <tr className="border-b border-[#E5E5E0] text-left text-[10px] tracking-luxury text-[#707070] uppercase">
                  <th className="px-2 py-2 font-normal bg-white w-8"></th>
                  <th className="px-3 py-2 font-normal bg-white">Codice Prodotto</th>
                  <th className="px-3 py-2 font-normal bg-white">Prodotto</th>
                  <th className="px-3 py-2 font-normal bg-white">Tipo</th>
                  <th className="px-3 py-2 font-normal bg-white">Brand</th>
                  <th className="px-3 py-2 font-normal bg-white">Nome prodotto Fornitore</th>
                  <th className="px-3 py-2 font-normal bg-white">Codice Fornitore</th>
                  <th className="px-3 py-2 font-normal bg-white">Formato</th>
                  <th className="px-3 py-2 font-normal bg-white w-28">Quantità</th>
                  <th className="px-3 py-2 font-normal bg-white w-24">UDM</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.length === 0 ? (
                  <tr><td colSpan={10} className="px-3 py-8 text-center text-sm text-[#707070]" data-testid="order-products-empty-filtered">Nessun prodotto corrisponde ai filtri selezionati.</td></tr>
                ) : filteredProducts.map(p => {
                  const r = rowFor(p);
                  const key = associationKey(p);
                  return (
                    <tr key={key} className={`border-b border-[#F0F0EA] ${r.checked ? "bg-[#FAFAF7]" : ""}`} data-testid={`order-product-${key}`}>
                      <td className="px-2 py-2">
                        <input type="checkbox" checked={!!r.checked} onChange={()=>toggleRow(p)} data-testid={`order-check-${key}`} />
                      </td>
                      <td className="px-3 py-2 text-[#707070] font-mono text-xs">{p.code || "—"}</td>
                      <td className="px-3 py-2 text-[#1A1A1A]">{p.name || <span className="text-[#B0B0B0]">—</span>}</td>
                      <td className="px-3 py-2"><RoleBadge role={p.supplier_role} testId={`order-role-${key}`} /></td>
                      <td className="px-3 py-2 text-[#707070]">{brandMap[p.brand_id]?.name || <span className="text-[#B0B0B0]">—</span>}</td>
                      <td className="px-3 py-2 text-[#1A1A1A]">{p.supplier_product_name || <span className="text-[#B0B0B0]">—</span>}</td>
                      <td className="px-3 py-2 text-[#707070] font-mono text-xs">{p.supplier_raw_material_code || "—"}</td>
                      <td className="px-3 py-2 text-[#707070]">{p.supplier_format || "—"}</td>
                      <td className="px-3 py-2">
                        <input type="number" min="0" step="any" className="input-luxury !py-1.5 w-24" placeholder="0"
                               disabled={!r.checked}
                               value={r.quantity ?? ""}
                               onChange={e=>setRow(key, { quantity: e.target.value })}
                               data-testid={`order-qty-${key}`} />
                      </td>
                      <td className="px-3 py-2">
                        <div className={r.checked ? "" : "opacity-40 pointer-events-none"}>
                          <UdmSelect value={r.udm ?? (p.supplier_order_udm || "")} onChange={e=>setRow(key, { udm: e.target.value })} testId={`order-udm-${key}`} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] tracking-luxury text-[#707070] uppercase">Righe personalizzate (prodotti non a catalogo)</div>
          <button onClick={addCustom} className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-[#E5E5E0] hover:border-[#C5B39B] transition" data-testid="order-add-custom">
            <Plus className="w-3 h-3"/> Aggiungi riga
          </button>
        </div>
        {customRows.length > 0 && (
          <div className="border border-[#E5E5E0] mb-5 overflow-x-auto" data-testid="order-custom-lines-scroll">
            <table className="w-full min-w-[52rem] text-sm">
              <thead>
                <tr className="border-b border-[#E5E5E0] text-left text-[10px] tracking-luxury text-[#707070] uppercase">
                  <th className="px-3 py-2 font-normal">Nome Prodotto</th>
                  <th className="px-3 py-2 font-normal">Codice</th>
                  <th className="px-3 py-2 font-normal w-40" data-testid="order-custom-role-header">Tipologia</th>
                  <th className="px-3 py-2 font-normal">Formato</th>
                  <th className="px-3 py-2 font-normal w-28">Quantità</th>
                  <th className="px-3 py-2 font-normal w-24">UDM</th>
                  <th className="px-2 py-2 font-normal w-8"></th>
                </tr>
              </thead>
              <tbody>
                {customRows.map((c, i) => (
                  <tr key={i} className="border-b border-[#F0F0EA]" data-testid={`order-custom-row-${i}`}>
                    <td className="px-3 py-2"><input className="input-luxury !py-1.5" value={c.name} placeholder="Nome" onChange={e=>setCustom(i,{name:e.target.value})} data-testid={`order-custom-name-${i}`} /></td>
                    <td className="px-3 py-2"><input className="input-luxury !py-1.5" value={c.code} placeholder="Codice" onChange={e=>setCustom(i,{code:e.target.value})} data-testid={`order-custom-code-${i}`} /></td>
                    <td className="px-3 py-2">
                      <select value={c.supplier_role} aria-label={`Tipologia prodotto riga ${i + 1}`}
                              aria-invalid={!!formError && !c.supplier_role}
                              aria-describedby={formError && !c.supplier_role ? "order-form-error" : undefined}
                              className={`input-luxury !py-1.5 !px-2 min-w-[9rem] ${!c.supplier_role ? "product-ux-select-empty" : ""}`}
                              onFocus={e=>keepRoleVisible(e.currentTarget)}
                              onChange={e=>{ setCustom(i, { supplier_role: e.target.value }); setFormError(""); }}
                              data-testid={`order-custom-role-${i}`}>
                        <option value="">Seleziona tipo</option>
                        <option value="raw_material">Materia Prima</option>
                        <option value="packaging">Packaging</option>
                        <option value="custom">Altro</option>
                      </select>
                    </td>
                    <td className="px-3 py-2"><input className="input-luxury !py-1.5" value={c.format} placeholder="Formato" onChange={e=>setCustom(i,{format:e.target.value})} data-testid={`order-custom-format-${i}`} /></td>
                    <td className="px-3 py-2"><input type="number" min="0" step="any" className="input-luxury !py-1.5 w-24" placeholder="0" value={c.quantity} onChange={e=>setCustom(i,{quantity:e.target.value})} data-testid={`order-custom-qty-${i}`} /></td>
                    <td className="px-3 py-2"><UdmSelect value={c.udm} onChange={e=>setCustom(i,{udm:e.target.value})} testId={`order-custom-udm-${i}`} /></td>
                    <td className="px-2 py-2 text-right">
                      <button onClick={()=>removeCustom(i)} className="text-[#8C3A3A] hover:opacity-70" title="Rimuovi" data-testid={`order-custom-remove-${i}`}><Trash2 className="w-3.5 h-3.5"/></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mb-4">
          <label className="label-tiny">Note per il Fornitore</label>
          <textarea className="input-luxury min-h-[80px]" data-testid="order-notes"
                    value={notes} onChange={e=>setNotes(e.target.value)}
                    placeholder="Indicazioni su consegna, urgenze, riferimenti..." />
        </div>

        {formError && <p role="alert" id="order-form-error" className="text-sm text-[#8C3A3A] mb-4" data-testid="order-form-error">{formError}</p>}

        <div className="flex justify-end gap-3">
          {Object.values(rows).some(r => r?.checked) && (
            <button onClick={()=>setRows({})} className="text-xs text-[#8C3A3A] hover:opacity-70 mr-auto" data-testid="order-clear-selection">
              Pulisci selezione
            </button>
          )}
          <button onClick={saveDraft} disabled={busy} className="btn-ghost text-sm inline-flex items-center gap-2" data-testid="order-save-draft">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : null} Salva bozza
          </button>
          <button onClick={openSendFromForm} disabled={busy || emailChecking} className="btn-primary text-sm inline-flex items-center gap-2" data-testid="order-send">
            {emailChecking ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Send className="w-3.5 h-3.5"/>} Invia ordine
          </button>
        </div>
        {renderSendModal()}
        {renderOrderEmailPrompt()}
        {confirmBack && (
          <UnsavedDecisionDialog
            prompt={orderPrompt} busy={busy} onStay={() => setConfirmBack(false)}
            onDiscard={discardAndBack} onSave={saveAndBack}
            testIds={{ modal: "order-unsaved-modal", description: "order-unsaved-description", error: "order-unsaved-error",
              stay: "order-unsaved-stay-btn", discard: "order-unsaved-cancel-btn", save: "order-unsaved-save-btn" }}
          />
        )}
      </div>
    );
  }

  // ----------------------------- DETAIL -----------------------------
  if (view === "detail" && detail) {
    return (
      <div data-testid="order-detail">
        <div className="flex items-center justify-between mb-4">
          <button onClick={()=>setView("list")} className="inline-flex items-center gap-1.5 text-sm text-[#707070] hover:text-[#1A1A1A]" data-testid="order-detail-back">
            <ArrowLeft className="w-3.5 h-3.5"/> Torna allo storico
          </button>
          <div className="flex items-center gap-3">
            {!subscriptionDisabled && <button onClick={()=>downloadPdf(detail)} className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 border border-[#E5E5E0] hover:border-[#C5B39B] transition" data-testid={`order-pdf-${detail.id}`}>
              <Download className="w-3.5 h-3.5"/> Scarica PDF
            </button>}
            <StatusBadge status={detail.status} testId={`order-status-${detail.status}-detail-${detail.id}`}/>
          </div>
        </div>
        <div className="text-xs text-[#707070] mb-3">
          Creato il {fmtDate(detail.created_at)}
          {detail.status === "sent" && detail.sent_at ? ` · Inviato il ${fmtDate(detail.sent_at)} a ${detail.recipient_email || ""}` : ""}
        </div>
        <div className="border border-[#E5E5E0] max-h-[42vh] overflow-auto mb-4">
          <table className="w-full min-w-[44rem] text-sm">
            <thead className="sticky top-0 bg-white z-10">
              <tr className="border-b border-[#E5E5E0] text-left text-[10px] tracking-luxury text-[#707070] uppercase">
                <th className="px-3 py-2 font-normal bg-white">Codice Prodotto</th>
                <th className="px-3 py-2 font-normal bg-white">Prodotto</th>
                <th className="px-3 py-2 font-normal bg-white">Tipo</th>
                <th className="px-3 py-2 font-normal bg-white">Nome prodotto Fornitore</th>
                <th className="px-3 py-2 font-normal bg-white">Codice Fornitore</th>
                <th className="px-3 py-2 font-normal bg-white">Formato</th>
                <th className="px-3 py-2 font-normal bg-white text-right">Quantità</th>
              </tr>
            </thead>
            <tbody>
              {(detail.items || []).map((it, i) => (
                <tr key={i} className="border-b border-[#F0F0EA]">
                  <td className="px-3 py-2 text-[#707070] font-mono text-xs">{it.product_code || "—"}</td>
                  <td className="px-3 py-2 text-[#1A1A1A]">{it.product_name || <span className="text-[#B0B0B0]">—</span>}</td>
                  <td className="px-3 py-2"><RoleBadge role={showCustomRole(it)} testId={`order-detail-role-${i}`} /></td>
                  <td className="px-3 py-2 text-[#1A1A1A]">{it.supplier_product_name || <span className="text-[#B0B0B0]">—</span>}</td>
                  <td className="px-3 py-2 text-[#707070] font-mono text-xs">{it.supplier_code || "—"}</td>
                  <td className="px-3 py-2 text-[#707070]">{it.format || "—"}</td>
                  <td className="px-3 py-2 text-[#1A1A1A] text-right whitespace-nowrap">{it.quantity} {it.udm || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {(detail.notes || "").trim() && (
          <div className="p-4 bg-[#FAF9F6] border-l-2 border-[#C5B39B] mb-2">
            <div className="text-[10px] tracking-luxury text-[#707070] uppercase mb-1">Note per il fornitore</div>
            <div className="text-sm text-[#1A1A1A] whitespace-pre-wrap">{detail.notes}</div>
          </div>
        )}
      </div>
    );
  }

  // ------------------------------ LIST ------------------------------
  return (
    <div data-testid="orders-list">
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-[#707070]">{orders.length} ordin{orders.length === 1 ? "e" : "i"}</div>
        {canWrite && (
          <button onClick={openNew} className="btn-primary text-sm inline-flex items-center gap-2" data-testid="order-new-btn">
            <Plus className="w-3.5 h-3.5"/> Nuovo Ordine
          </button>
        )}
      </div>
      {orders.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-[#E5E5E0]" data-testid="orders-empty">
          <ClipboardList className="w-8 h-8 mx-auto text-[#C5B39B] mb-3" strokeWidth={1.5}/>
          <div className="text-sm text-[#707070]">Nessun ordine. Crea il primo con "Nuovo Ordine".</div>
        </div>
      ) : (
        <div className="overflow-x-auto"><table className="w-full min-w-[38rem] text-sm">
          <thead>
            <tr className="border-b border-[#E5E5E0] text-left text-[10px] tracking-luxury text-[#707070] uppercase">
              <SortableTh field="order_number" label="N. Ordine" sortField={sortField} sortDir={sortDir} onSort={toggleSort} className="px-3 py-2 font-normal" />
              <SortableTh field="created_at" label="Data" sortField={sortField} sortDir={sortDir} onSort={toggleSort} className="px-3 py-2 font-normal" />
              <SortableTh field="status" label="Stato" sortField={sortField} sortDir={sortDir} onSort={toggleSort} className="px-3 py-2 font-normal" />
              <SortableTh field="items" label="Articoli" sortField={sortField} sortDir={sortDir} onSort={toggleSort} className="px-3 py-2 font-normal" />
              <th className="px-3 py-2 font-normal w-40 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {sortedOrders.map(o => (
              <tr key={o.id} className="border-b border-[#F0F0EA] hover:bg-[#FAFAF7] group" data-testid={`order-row-${o.id}`}>
                <td className="px-3 py-2.5 text-[#1A1A1A] font-mono text-xs">{o.order_number || "—"}</td>
                <td className="px-3 py-2.5 text-[#1A1A1A]">{fmtDate(o.created_at)}</td>
                <td className="px-3 py-2.5"><StatusBadge status={o.status} testId={`order-status-${o.status}-${o.id}`}/></td>
                <td className="px-3 py-2.5 text-[#707070]">{(o.items || []).length}</td>
                <td className="px-3 py-2.5">
                  <div className="flex justify-end items-center gap-3">
                    {!subscriptionDisabled && <button onClick={()=>downloadPdf(o)} className="text-[#707070] hover:text-[#C5B39B]" title="Scarica PDF" data-testid={`order-pdf-${o.id}`}>
                      <Download className="w-3.5 h-3.5"/>
                    </button>}
                    {o.status === "draft" && canWrite ? (
                      <>
                        <button onClick={()=>openEdit(o)} className="text-[#707070] hover:text-[#1A1A1A]" title="Modifica bozza" data-testid={`order-edit-${o.id}`}>
                          <Edit2 className="w-3.5 h-3.5"/>
                        </button>
                        <button onClick={()=>openSendExisting(o.id)} disabled={busy || emailChecking} className="text-[#2F6B45] hover:opacity-70 inline-flex items-center gap-1 text-xs" title="Invia ordine" data-testid={`order-send-${o.id}`}>
                          <Send className="w-3.5 h-3.5"/> Invia
                        </button>
                      </>
                    ) : (
                      <button onClick={()=>openDetail(o)} className="text-[#707070] hover:text-[#1A1A1A]" title="Visualizza" data-testid={`order-view-${o.id}`}>
                        <Eye className="w-3.5 h-3.5"/>
                      </button>
                    )}
                    {canWrite && (
                      <button onClick={()=>removeOrder(o)} className="text-[#8C3A3A] hover:opacity-70" title="Elimina" data-testid={`order-delete-${o.id}`}>
                        <Trash2 className="w-3.5 h-3.5"/>
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}
      {renderSendModal()}
      {renderOrderEmailPrompt()}
    </div>
  );
}
